import { CheckCircle, UserCircle } from '@phosphor-icons/react';
import type { ImportBatchRow, RowChangeSet, RowChoice } from '@discovr/types';
import { decisionsForAll } from '@discovr/features';
import { Button } from '../../../components/ui/button';
import { decisionsChangeSet, existingCreatorsSummary, type ExistingCreatorsSummary } from './reviewGridDraft';

const ICON_SIZE = 16;
const BAR_CLASS = 'flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted px-3 py-2 text-body-sm-regular';

const BULK_MESSAGE: Record<RowChoice, string> = {
  current: 'All existing creators set to current',
  sheet: 'All existing creators set to sheet',
  undecided: 'Existing creators set back to undecided',
};

interface ExistingCreatorsBarProps {
  rows: ImportBatchRow[];
  busy: boolean;
  onSaveRows: (changes: RowChangeSet, message: string) => void;
}

function alreadyInCampaignText(total: number): string {
  const verb = total === 1 ? 'creator is' : 'creators are';
  return `${total} ${verb} already in this campaign.`;
}

function decidedText(summary: ExistingCreatorsSummary): string {
  return `All ${summary.total} decided · ${summary.willUpdate} will update, ${summary.keepingCurrent} keeping current ·`;
}

export function ExistingCreatorsBar({ rows, busy, onSaveRows }: ExistingCreatorsBarProps) {
  const summary = existingCreatorsSummary(rows);
  if (summary.total === 0) return null;

  function decideAll(choice: RowChoice) {
    onSaveRows(decisionsChangeSet(decisionsForAll(rows, choice)), BULK_MESSAGE[choice]);
  }

  if (summary.undecided > 0) {
    return (
      <div className={BAR_CLASS}>
        <UserCircle size={ICON_SIZE} aria-hidden="true" className="text-muted-foreground" />
        <p className="min-w-0 flex-1">
          <span className="text-body-sm-medium">{alreadyInCampaignText(summary.total)}</span>{' '}
          <span className="text-muted-foreground">Pick a version for each, or for all at once.</span>
        </p>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => decideAll('current')}>
          Keep all current
        </Button>
        <Button type="button" size="sm" disabled={busy} onClick={() => decideAll('sheet')}>
          Use sheet for all
        </Button>
      </div>
    );
  }

  return (
    <div className={BAR_CLASS}>
      <CheckCircle size={ICON_SIZE} aria-hidden="true" className="text-success" />
      <p className="min-w-0 flex-1">
        {decidedText(summary)}{' '}
        <Button type="button" variant="link" size="xs" className="h-auto px-0" disabled={busy} onClick={() => decideAll('undecided')}>
          Change
        </Button>
      </p>
    </div>
  );
}

